import { useEffect, useState } from "react";
import { Download, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PaymentPanel, PayBadge } from "./PaymentPanel";

const STATUSES = ["new", "contacted", "closed"];
const STATUS_STYLE = { new: "bg-wine text-ivory", contacted: "bg-champagne text-charcoal", closed: "bg-line text-charcoal" };
const FIELDS = [["brand_name", "Brand"], ["email", "Email"], ["phone", "Phone / WhatsApp"], ["country", "Country"], ["website", "Website / Instagram"], ["industry", "Industry"], ["service", "Service"], ["project_type", "Project type"], ["budget", "Budget"], ["timeline", "Timeline"], ["description", "Description"]];

const saveBlob = (data, name) => {
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};

const StatusSelect = ({ value, onChange, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} onClick={(e) => e.stopPropagation()} data-testid={testid} className={`px-2 py-1 text-xs uppercase tracking-[0.1em] ${STATUS_STYLE[value]}`}>
    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
  </select>
);

export const EnquiriesTab = () => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  const load = () => api.get("/admin/enquiries").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errorText(e)));
  useEffect(() => { load().then(() => api.post("/admin/payments/sync").then(load).catch(() => {})); }, []);
  const replace = (e) => { setItems((list) => list.map((x) => (x.id === e.id ? e : x))); setActive(e); };

  const setStatus = async (e, status) => {
    const { data } = await api.patch(`/admin/enquiries/${e.id}`, { status });
    setItems((list) => list.map((x) => (x.id === e.id ? data : x)));
    if (active?.id === e.id) setActive(data);
  };
  const exportCsv = async () => {
    const { data } = await api.get("/admin/enquiries/export", { responseType: "blob" });
    saveBlob(data, "bwi-enquiries.csv");
  };
  const downloadBrief = async (b) => {
    try {
      const { data } = await api.get(b.url.replace(/^\/api/, ""), { responseType: "blob" });
      saveBlob(data, b.name || "brief");
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  return (
    <div className="border border-line bg-ivory">
      <div className="flex items-center justify-between border-b border-line p-5">
        <div>
          <h2 className="font-serif text-2xl text-wine">Quote requests</h2>
          <p className="text-xs text-muted-ink">{items.filter((e) => e.status === "new").length} new · {items.length} total</p>
        </div>
        <button onClick={exportCsv} className="btn btn-secondary !px-4 !py-2.5" data-testid="admin-export-csv-btn"><Download size={14} /> Export CSV</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[680px] text-left text-sm" data-testid="admin-enquiries-table">
          <thead className="text-[0.65rem] uppercase tracking-[0.15em] text-muted-ink">
            <tr className="border-b border-line">{["Received", "Name", "Brand", "Service", "Payment", "Status"].map((h) => <th key={h} className="px-5 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} onClick={() => setActive(e)} className="cursor-pointer border-b border-line last:border-0 hover:bg-sand" data-testid={`admin-enquiry-row-${e.id}`}>
                <td className="whitespace-nowrap px-5 py-4 text-muted-ink">{new Date(e.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</td>
                <td className="px-5 py-4">{e.full_name}</td>
                <td className="px-5 py-4">{e.brand_name}{e.brief_file && <Paperclip size={12} className="ml-1 inline text-wine" />}</td>
                <td className="px-5 py-4">{e.service}</td>
                <td className="px-5 py-4">{e.payments?.length ? <PayBadge status={e.payments[e.payments.length - 1].status} testid={`admin-enquiry-payment-${e.id}`} /> : <span className="text-muted-ink">—</span>}</td>
                <td className="px-5 py-4"><StatusSelect value={e.status} onChange={(s) => setStatus(e, s)} testid={`admin-enquiry-status-${e.id}`} /></td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={6} className="p-10 text-center text-muted-ink">No enquiries yet. They will appear here as soon as brands get in touch.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto bg-ivory" data-testid="admin-enquiry-detail">
          {active && (
            <>
              <DialogHeader><DialogTitle className="font-serif text-3xl font-normal text-wine">{active.full_name}</DialogTitle></DialogHeader>
              <div className="flex items-center gap-3 text-xs text-muted-ink">
                <StatusSelect value={active.status} onChange={(s) => setStatus(active, s)} testid="admin-enquiry-detail-status" />
                Received {new Date(active.created_at).toLocaleString()}
              </div>
              <dl className="mt-2 divide-y divide-line">
                {FIELDS.map(([k, label]) => active[k] ? (
                  <div key={k} className="grid grid-cols-3 gap-3 py-3 text-sm">
                    <dt className="text-muted-ink">{label}</dt>
                    <dd className="col-span-2 whitespace-pre-line break-words">{active[k]}</dd>
                  </div>
                ) : null)}
              </dl>
              {active.brief_file && (
                <button onClick={() => downloadBrief(active.brief_file)} className="btn btn-secondary mt-2 self-start" data-testid="admin-download-brief"><Download size={14} /> {active.brief_file.name}</button>
              )}
              <PaymentPanel enquiry={active} onChange={replace} />
              <a href={`mailto:${active.email}?subject=${encodeURIComponent("Your enquiry with BWI")}`} className="btn btn-primary mt-2 justify-center" data-testid="admin-reply-email">Reply by email</a>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
